import {createId} from "../../../../../../utils/projectFactories";

function uid(prefix = "id") {
    return `${prefix}_${Math.random().toString(36).slice(2, 9)}_${Date.now().toString(36)}`;
}

export function createEmptyBlocksContainer() {
    return { blocks: [] };
}

export function createEmptyTextBlock() {
    return {
        id: uid("block"),
        type: "text",
        config: { text: "" }
    };
}

export function createEmptyCodeBlock() {
    return {
        id: uid("block"),
        type: "code",
        config: {
            language: "javascript",
            code: ""
        }
    };
}

export function createEmptyLinkBlock() {
    return {
        id: createId("block"),
        type: "link",
        config: {
            label: "",
            url: ""
        }
    };
}


export function createEmptyHint() {
    return {
        id: uid("hint"),
        energyCost: 0,
        blocks: []
    };
}


export function createEmptyEditor({ language = "python" } = {}) {
    return {
        id: uid("editor"),
        language,
        starterCode: "",
        readOnly: false,
        fileName: "",
    };
}

/**
 * NOTE: schema change you confirmed:
 * - program/sql => editorId
 * - web => editorIds: {html, css, js}
 */
export function createEmptyRunTarget({ resultType = "program", editorId } = {}) {
    const base = {
        id: uid("target"),
        runner: resultType === "sql" ? "sqlite" : resultType === "web" ? "web" : "python",
        resultType, // "program" | "sql" | "web"
    };

    if (resultType === "web") {
        return {
            ...base,
            editorIds: { html: "", css: "", js: "" },
        };
    }

    return {
        ...base,
        editorId: editorId || "",
    };
}

export function createEmptyValidation() {
    return {
        mode: "all", // "all" | "any"
        checks: [],
    };
}

/**
 * Minimal default check. You will likely change defaults per resultType later.
 */
export function createEmptyValidationCheck({ targetId = "" } = {}) {
    return {
        id: uid("check"),
        targetId,
        kind: "stdout_exact",
        expected: "",
    };
}

export function createEmptyProjectStep({ defaultLanguage = "python" } = {}) {
    const editor = createEmptyEditor({ language: defaultLanguage });

    const step = {
        id: uid("step"),
        instruction: createEmptyBlocksContainer(),

        editors: [editor],

        run: {
            targets: [
                createEmptyRunTarget({
                    resultType: "program",
                    editorId: editor.id,
                }),
            ],
        },

        validation: createEmptyValidation(),

        feedback: {
            correct: "",
            incorrect: "",
        },

        hints: [],

        explain: createEmptyBlocksContainer(),

        // solution is per-editor. You can add later in UI.
        solution: {
            editors: {
                // [editor.id]: { code: "" }
            },
        },
    };

    // add one default check referencing the first target
    const firstTargetId = step.run.targets[0].id;
    step.validation.checks.push(createEmptyValidationCheck({ targetId: firstTargetId }));

    return step;
}

export function createEmptyProjectPart() {
    return {
        id: uid("part"),
        name: "New Part",
        steps: [createEmptyProjectStep()],
    };
}
